// combatpro/views/EncounterDashboardView.js
//
// Stage 119 (Encounter Dashboard & 12-Slot Encounter Store).
//
// Faithful React port of the legacy dashboard render layer (battletracker.html
// viRenderDashboard 10651, viRenderEncCard 10663, viRenderLockedCard 10715) and
// the edit modal (viEncEdit 10785 / viEncModalSave 10810). Markup, class names,
// text, emoji, field set, and refresh semantics are preserved; the imperative
// innerHTML string-building and DOM handlers are replaced by declarative React
// (the approved Category-E substitution). No logic is duplicated: metrics, tier
// math, and status/theme data come from the engine and context; CRUD / switch /
// meta-edit run through the CombatContext handlers.
//
// STATE presentation data (VI_STATE, 10335-10341) is view-only presentation and
// lives here as a local constant (it is not engine logic). VI_THEMES is imported
// from the engine (the store engine already owns it). Tier gating reuses the
// engine unlockedSlots / slotLocked with the tier from context - no hardcoded
// slot counts, no duplicated calculation.
//
// Deferred (untouched here): live synchronisation, WebSockets, session history.

import React, { useContext, useState } from 'react';
import TemplatesPanel from '../runtime/TemplatesPanel';
import GeneratedFamilySection from '../runtime/GeneratedFamilySection';
import { CombatContext } from '../contexts/CombatContext';
import {
  monsterCount,
  playerCount,
  hpPercent,
  unlockedSlots,
  slotLocked,
  THEMES,
  ENC_STATUSES,
  countSavedEncounters
} from '../encounter/encounterStoreEngine';

// VI_STATE (10335-10341): status -> presentation metadata. View-only.
var STATE_META = {
  'Empty':       { key: 'empty',       color: '#6B7685', emoji: '\u26AA', label: 'Empty' },
  'Design':      { key: 'design',      color: '#3B82F6', emoji: '\uD83D\uDD35', label: 'Design' },
  'Ready':       { key: 'ready',       color: '#22C55E', emoji: '\uD83D\uDFE2', label: 'Ready' },
  'In Progress': { key: 'in-progress', color: '#EF4444', emoji: '\uD83D\uDD34', label: 'In Progress' },
  'Completed':   { key: 'completed',   color: '#C9950C', emoji: '\uD83D\uDFE1', label: 'Completed' },
  'Archived':    { key: 'archived',    color: '#3A434F', emoji: '\u26AB', label: 'Archived' }
};

// viState (10363) / viStatusClass (10364).
function stateMeta(status) {
  return STATE_META[status] || STATE_META['Empty'];
}
function statusClass(status) {
  return 's-' + stateMeta(status).key;
}

// viFmtTime (10365): local time HH:MM, or 'Never'.
function fmtTime(ts) {
  if (!ts) { return 'Never'; }
  try {
    return new Date(ts).toLocaleString([], { hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return 'Unknown';
  }
}

// Empty edit-form shape.
function emptyForm() {
  return {
    name: '', location: '', difficulty: '', campaign: '', partyLevel: '',
    status: 'Empty', description: '', notes: '', colorTheme: 'default', tags: ''
  };
}

// viRenderLockedCard (10715): a locked slot with the upgrade prompt.
function LockedCard(props) {
  var i = props.index;
  var need = i < 8 ? 'Plus' : 'Ultimate';
  return (
    <div className="vi-enc-card is-locked">
      <div className="vi-enc-lock-overlay">
        <div className="vi-enc-lock-icon">{'\uD83D\uDD12'}</div>
        <div className="vi-enc-lock-title">{'Slot ' + (i + 1) + ' locked'}</div>
        <div className="vi-enc-lock-sub">Upgrade to <b>{need}</b> to unlock this encounter slot.</div>
        <button className="vi-enc-upgrade" onClick={props.onUpgrade}>View plans</button>
      </div>
    </div>
  );
}

// viRenderEncCard (10663): one encounter slot card.
function EncounterCard(props) {
  var e = props.encounter;
  var i = props.index;
  var m = e.meta || {};
  var st = stateMeta(m.status);
  var isActive = props.isActive;
  var accent = (THEMES[m.colorTheme] || st.color);
  var mc = monsterCount(e.combat);
  var pc = playerCount(e.combat);
  var hp = hpPercent(e.combat);
  var round = (e.combat && e.combat.currentRound) ? e.combat.currentRound : 1;
  var isEmpty = (m.status === 'Empty');
  var saved = !!m.saved;
  var tags = (Array.isArray(m.tags) && m.tags.length) ? m.tags : [];
  var cardClass = 'vi-enc-card ' + (isActive ? 'is-active ' : '') + statusClass(m.status);

  return (
    <div className={cardClass} style={{ '--accent': accent }}>
      <div className="vi-enc-accent"></div>
      <div className="vi-enc-card-head">
        <div className="vi-enc-card-name">{m.name || ('Encounter ' + (i + 1))}</div>
        <span className={'vi-enc-badge ' + statusClass(m.status)}>{st.emoji + ' ' + st.label}</span>
        {saved ? <span className="vi-enc-badge cp-badge-saved">{'\ud83d\udcbe Saved'}</span> : null}
      </div>
      <div className="vi-enc-card-slot">{'Slot ' + (i + 1) + (isActive ? ' \u00b7 Active now' : '')}</div>
      {isEmpty ? (
        <div className="vi-enc-empty-body">Empty slot. Load it to start building, or edit its details.</div>
      ) : (
        <React.Fragment>
          <div className="vi-enc-stats">
            <span title="Location">{'\uD83D\uDCCD ' + (m.location || '\u2014')}</span>
            <span title="Difficulty">{'\u2694\uFE0F ' + (m.difficulty || '\u2014')}</span>
            <span title="Players">{'\uD83D\uDC65 ' + pc + ' Players'}</span>
            <span title="Monsters">{'\uD83D\uDC79 ' + mc + ' Monsters'}</span>
            <span title="HP remaining">{'\u2764\uFE0F ' + (hp === null ? '\u2014' : (hp + '% HP'))}</span>
            <span title="Round">{'\u23F1 Round ' + round}</span>
          </div>
          {tags.length ? (
            <div className="vi-enc-tags">
              {tags.map(function (t, ti) { return <span className="vi-enc-tag" key={ti}>{t}</span>; })}
            </div>
          ) : null}
          {m.description ? (
            <div className="vi-enc-desc">{m.description}</div>
          ) : null}
          <div className="vi-enc-modline">{'\uD83D\uDD53 Last modified ' + fmtTime(m.lastModified)}</div>
        </React.Fragment>
      )}
      <div className="vi-enc-card-actions">
        <button className={'vi-enc-continue' + (isActive ? ' is-on' : '')} onClick={function () { props.onContinue(i); }}>Continue</button>
        <button onClick={function () { props.onLoad(i); }}>Load</button>
        <button onClick={function () { props.onEdit(i); }}>Edit</button>
        <button onClick={function () { props.onDuplicate(i); }}>Duplicate</button>
        <button onClick={function () { props.onArchive(i); }}>Archive</button>
        <button onClick={function () { props.onReset(i); }}>Reset</button>
        <button className="vi-enc-danger" onClick={function () { props.onDelete(i); }}>Delete</button>
        {!isEmpty ? (<button className={'cp-slot-save' + (saved ? ' is-saved' : '')} onClick={function () { props.onToggleSaved(i, !saved); }}>{saved ? 'Saved \u2713' : 'Save'}</button>) : null}
      </div>
    </div>
  );
}

function EncounterDashboardView() {
  const ctx = useContext(CombatContext);
  const state = ctx.state;
  const tier = ctx.encounterTier;
  const store = state.encounterStore;
  const encounters = (store && Array.isArray(store.encounters)) ? store.encounters : [];
  const activeIndex = (store && typeof store.activeIndex === 'number') ? store.activeIndex : 0;

  // Edit-modal state (React equivalent of viEncEdit populating the DOM modal and
  // viEncEditingIndex; viEncModalSave commits through ctx.saveEncounterMeta).
  const editHook = useState(-1);
  const editingIndex = editHook[0];
  const setEditingIndex = editHook[1];
  const formHook = useState(emptyForm());
  const form = formHook[0];
  const setForm = formHook[1];

  // viEncEdit (10785): locked-guard, then open the modal populated from the slot.
  function openEdit(i) {
    if (slotLocked(i, tier)) { ctx.showUpgrade(); return; }
    var enc = encounters[i];
    if (!enc) { return; }
    var mm = enc.meta || {};
    setForm({
      name: (mm.name == null ? '' : mm.name),
      location: (mm.location == null ? '' : mm.location),
      difficulty: (mm.difficulty == null ? '' : mm.difficulty),
      campaign: (mm.campaign == null ? '' : mm.campaign),
      partyLevel: (mm.partyLevel == null ? '' : mm.partyLevel),
      status: (mm.status == null ? 'Empty' : mm.status),
      description: (mm.description == null ? '' : mm.description),
      notes: (mm.notes == null ? '' : mm.notes),
      colorTheme: (mm.colorTheme || 'default'),
      tags: (Array.isArray(mm.tags) ? mm.tags.join(', ') : '')
    });
    setEditingIndex(i);
  }
  // viEncModalClose (10805).
  function closeEdit() {
    setEditingIndex(-1);
  }
  // viEncModalSave (10810): commit through the context handler (which owns the
  // guards), then close.
  function saveEdit() {
    if (editingIndex < 0) { closeEdit(); return; }
    ctx.saveEncounterMeta(editingIndex, form);
    closeEdit();
  }
  function setField(key, val) {
    setForm(function (previous) {
      var next = Object.assign({}, previous);
      next[key] = val;
      return next;
    });
  }

  var unlocked = unlockedSlots(tier);
  var tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);

  return (
    <div className="vi-view" id="view-encounter-dashboard">
      <div className="vi-enc-dash-head">
        <div>
          <h2>Encounter Dashboard</h2>
          <div className="vi-enc-dash-helper">Prepare, organise and switch between your encounters. Every change saves automatically to the cloud.</div>
        </div>
        <button className="vi-enc-dash-link" onClick={function () { ctx.switchView('view-encounter'); }}>{'<'} Back to Combat</button>
      </div>
      <div className="vi-enc-dash-tier" id="vi-enc-dash-tier">
        {'Plan: '}<b>{tierLabel}</b>{' \u2022 ' + unlocked + ' of 12 encounter slots unlocked'}
      </div>
      <h3 className="cp-dash-section-title">Standard Encounters</h3>
      {(function () {
        var ent = state.entitlement ? state.entitlement : { perFamily: 2, total: 2, cloud: false };
        var counts = countSavedEncounters(store);
        return (
          <div className="cp-quota-bar">
            <span className="cp-quota-item">{'Saved Standard: '}<b>{(counts.standard || 0) + ' / ' + ent.perFamily}</b></span>
            <span className="cp-quota-item">{'Total saved (all families): '}<b>{counts.total + ' / ' + ent.total}</b></span>
            <span className="cp-quota-note">{ent.cloud ? ('Combat PRO Cloud - ' + ent.perFamily + ' per family') : 'Lifetime: 2 saved encounters total across Standard, Random and Wave. Upgrade to Cloud for more.'}</span>
          </div>
        );
      })()}
      <div className="vi-enc-dash-grid" id="vi-enc-dash-grid">
        {encounters.map(function (encounter, index) {
          if (slotLocked(index, tier)) {
            return <LockedCard key={index} index={index} onUpgrade={ctx.showUpgrade} />;
          }
          return (
            <EncounterCard
              key={index}
              index={index}
              encounter={encounter}
              isActive={index === activeIndex}
              onContinue={ctx.encContinue}
              onLoad={ctx.encLoad}
              onEdit={openEdit}
              onDuplicate={ctx.encDuplicate}
              onArchive={ctx.encArchive}
              onReset={ctx.encReset}
              onDelete={ctx.encDelete}
              onToggleSaved={function (ix, val) { ctx.setEncounterSaved('standard', ix, val); }}
            />
          );
        })}
      </div>

      <div className="cp-dash-section">
        <h3 className="cp-dash-section-title">Random Encounters</h3>
        <div className="cp-dash-section-helper">Generated surprise fights from the existing monster catalogue. Set the presets, then generate into any of the 12 Random slots.</div>
        <GeneratedFamilySection family="random" />
      </div>

      <div className="cp-dash-section">
        <h3 className="cp-dash-section-title">Wave Encounters</h3>
        <div className="cp-dash-section-helper">Complete multi-stage battles. Combat PRO builds the waves, combat groups and entry triggers automatically.</div>
        <GeneratedFamilySection family="wave" />
      </div>

      <TemplatesPanel />

      {editingIndex >= 0 ? (
        <div id="vi-enc-edit-modal" className="vi-enc-modal open" style={{ display: 'flex' }}>
          <div className="vi-enc-modal-inner">
            <div className="vi-enc-modal-head">
              <span id="vi-em-title">{'Edit Encounter \u2014 Slot ' + (editingIndex + 1)}</span>
              <button className="vi-enc-modal-x" onClick={closeEdit}>{'\u00d7'}</button>
            </div>
            <label>Name
              <input id="vi-em-name" value={form.name} onChange={function (ev) { setField('name', ev.target.value); }} />
            </label>
            <label>Location
              <input id="vi-em-loc" value={form.location} onChange={function (ev) { setField('location', ev.target.value); }} />
            </label>
            <label>Difficulty
              <input id="vi-em-diff" value={form.difficulty} onChange={function (ev) { setField('difficulty', ev.target.value); }} />
            </label>
            <label>Campaign
              <input id="vi-em-camp" value={form.campaign} onChange={function (ev) { setField('campaign', ev.target.value); }} />
            </label>
            <label>Party Level
              <input id="vi-em-level" value={form.partyLevel} onChange={function (ev) { setField('partyLevel', ev.target.value); }} />
            </label>
            <label>Status
              <select id="vi-em-status" value={form.status} onChange={function (ev) { setField('status', ev.target.value); }}>
                {ENC_STATUSES.map(function (s) { return <option key={s} value={s}>{s}</option>; })}
              </select>
            </label>
            <label>Description
              <textarea id="vi-em-desc" value={form.description} onChange={function (ev) { setField('description', ev.target.value); }} />
            </label>
            <label>Notes
              <textarea id="vi-em-notes" value={form.notes} onChange={function (ev) { setField('notes', ev.target.value); }} />
            </label>
            <label>Theme
              <select id="vi-em-theme" value={form.colorTheme} onChange={function (ev) { setField('colorTheme', ev.target.value); }}>
                {Object.keys(THEMES).map(function (k) { return <option key={k} value={k}>{k}</option>; })}
              </select>
            </label>
            <label>Tags
              <input id="vi-em-tags" value={form.tags} onChange={function (ev) { setField('tags', ev.target.value); }} />
            </label>
            <div className="vi-enc-modal-actions">
              <button className="vi-enc-modal-save" onClick={saveEdit}>Save</button>
              <button className="vi-enc-modal-cancel" onClick={closeEdit}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EncounterDashboardView;
